import { type ComponentProps, useState } from "react";
import { SlidersHorizontalIcon } from "lucide-react";

import type {
  FilterDefinitionType,
  FilterType,
  SortableDefinitionsType,
  SortableType,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { FilterChip } from "@/components/organisms/UtilityBar/FilterChip";
import { FilterChipPopper } from "@/components/organisms/UtilityBar/FilterChip/FilterChipPopper";
import { FilterChipStateProvider } from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { SortChip } from "@/components/organisms/UtilityBar/SortChip";
import { UtilityBarDraft } from "@/components/organisms/UtilityBar/UtilityBarDraft";
import { UtilityBarSearch } from "@/components/organisms/UtilityBar/UtilityBarSearch";
import { useAppRouteState } from "@/app/app/_/routing";
import { SEARCH_FILTER_KEY } from "@/graphql/filters";
import {
  CONNECTION_VIEWS,
  isConnectionView,
  type ConnectionViewConfig,
} from "@/hooks/useConnectionSetup";
import { useDevice } from "@/hooks/useDevice";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";

export function UtilityBar({
  buttons = [],
  includeSort = true,
  includeFilters = true,
  filters: controlledFilters,
  filterDefinitions: controlledFilterDefinitions,
  onFiltersChange,
  sortables,
  sortableDefinitions,
  onSortablesChange,
  isSearchable: isSearchableProp,
  disablePadding = false,
  compactFilters = false,
  filtersInPopper: filtersInPopperProp,
}: {
  buttons?: ComponentProps<typeof Button>[];
  includeSort?: boolean;
  includeFilters?: boolean;
  filters?: FilterType[];
  filterDefinitions?: Record<string, FilterDefinitionType>;
  onFiltersChange?: (filters: FilterType[]) => void;
  /** A sort of the bar's own, as ``filters`` is; the open list's otherwise. */
  sortables?: SortableType[];
  sortableDefinitions?: Record<string, SortableDefinitionsType>;
  onSortablesChange?: (sortables: SortableType[]) => void;
  /** Whether it offers a search, for a bar over some other list than the route's. */
  isSearchable?: boolean;
  disablePadding?: boolean;
  compactFilters?: boolean;
  /**
   * For a bar with no room to lay its filters out - a table's, in a record's
   * popper: it reads sort, filter, search on one line that never scrolls
   * sideways, and its filters are held in a popper behind the one button.
   */
  filtersInPopper?: boolean;
}) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // A phone - or a window as narrow - has no room to lay a list's filters out
  // either: they are held behind the one button there too, and the line
  // never scrolls sideways.
  const { isNarrow } = useDevice();
  const filtersInPopper = filtersInPopperProp ?? isNarrow;
  const globalFilters = State((state) => state.filters);
  const filters = controlledFilters ?? globalFilters;
  // Which view this bar belongs to decides whether it gets a search box: the
  // ledger lists are backed by a searchable model, reports and rules are not.
  // Read from the route rather than the store, which lags it by a render.
  const { view } = useAppRouteState();
  const isSearchable =
    isSearchableProp ??
    (isConnectionView(view) &&
      (CONNECTION_VIEWS[view] as ConnectionViewConfig).searchable === true);

  // The tab's locked filters come before the ones the user added.
  // ``filterIndex`` stays the position in ``filters`` itself — a chip removes
  // itself by it, so it cannot be the index within either of these halves.
  const chips = filters
    .map((filter, filterIndex) => ({ filter, filterIndex }))
    // The term rides in ``filters`` but is not a filter chip: no column backs
    // it, and it is typed in ``UtilityBarSearch``'s field instead.
    .filter(({ filter }) => filter.key !== SEARCH_FILTER_KEY);
  const lockedChips = chips.filter(({ filter }) => filter.isLocked === true);
  const unlockedChips = chips.filter(({ filter }) => filter.isLocked !== true);
  const isControlled =
    controlledFilters != null &&
    controlledFilterDefinitions != null &&
    onFiltersChange != null;

  const content = (
    <div
      // What holds a bar reaches it by this (the draft's box wraps it).
      data-utility-bar
      // A bar with its filters in a popper fits its line and never scrolls.
      // Disable horizontal scrollbar
      // No left inset: the subnav's own container already sets the left
      // edge, and stacking another one pushed the first chip — and the
      // search field ahead of it — visibly further in than the content
      // below. The right side keeps its padding.
      className={cn(
        "min-w-0",
        "flex flex-row gap-2 items-center",
        disablePadding ? "w-auto" : "w-[calc(100%)]",
        disablePadding ? "max-w-full" : "max-w-[calc(100%)]",
        "h-full",
        disablePadding ? "min-h-0" : "min-h-12",
        filtersInPopper
          ? "overflow-visible"
          : disablePadding
            ? "overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto overflow-y-visible",
        "[scrollbar-width:none] [-ms-overflow-style:none] pt-0 pb-0 pl-0 [&::-webkit-scrollbar]:hidden",
        disablePadding ? "pr-0" : "pr-2",
      )}
    >
      {filtersInPopper ? (
        <>
          {includeSort && <SortChip />}
          {includeFilters &&
            (chips.length === 0 ? (
              <FilterChipPopper />
            ) : (
              <Popper
                isHoverable={false}
                isClickable={true}
                isEscapeable={true}
                isOpenState={[isFiltersOpen, setIsFiltersOpen]}
                floatingStyle={{ padding: 0 }}
                anchor={
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="utility-bar-filters"
                    aria-label={`Filters, ${chips.length} set`}
                  >
                    <Icon
                      ReactIcon={SlidersHorizontalIcon}
                      className="text-muted-foreground"
                    />
                    Filters
                    <span className="text-muted-foreground">
                      {chips.length}
                    </span>
                  </Button>
                }
              >
                <div
                  data-testid="utility-bar-filters-popper"
                  className="flex flex-col items-start gap-1.5 p-2"
                >
                  {chips.map(({ filter, filterIndex }) => (
                    <FilterChip
                      key={filter.key}
                      filterKey={filter.key}
                      filter={filter}
                      filterIndex={filterIndex}
                      compact={compactFilters}
                    />
                  ))}
                  <FilterChipPopper />
                </div>
              </Popper>
            ))}
          {includeFilters && isSearchable && isControlled && (
            // What is left of the line, however little: the bar never scrolls.
            <div className="flex-1 min-w-0 flex">
              <UtilityBarSearch />
            </div>
          )}
        </>
      ) : (
        <>
          {includeSort && (
            <>
              <SortChip />
            </>
          )}
          {includeFilters && (
            <>
              {lockedChips.map(({ filter, filterIndex }) => (
                <FilterChip
                  key={filter.key}
                  filterKey={filter.key}
                  filter={filter}
                  filterIndex={filterIndex}
                  compact={compactFilters}
                />
              ))}
              {unlockedChips.map(({ filter, filterIndex }) => (
                <FilterChip
                  key={filter.key}
                  filterKey={filter.key}
                  filter={filter}
                  filterIndex={filterIndex}
                  compact={compactFilters}
                />
              ))}
              <FilterChipPopper />
              {/* The open list's search sits in the top bar, after its
              breadcrumbs; a bar over a list of its own carries that list's -
              after its sort and its filters, as on a phone. */}
              {isSearchable && isControlled && <UtilityBarSearch />}
              {isConnectionView(view) && !isControlled && (
                <UtilityBarDraft isSearchable={isSearchable} />
              )}
            </>
          )}
        </>
      )}
      {buttons.length > 0 && (
        <>
          <div className="grow" />
          <div className="min-w-px h-[calc(100%-12px)] bg-border rounded" />
        </>
      )}
      <div className="flex flex-row gap-2 min-w-fit">
        {buttons.map((props, index) => (
          <Button variant="ghost" size="sm" key={index} {...props} />
        ))}
      </div>
    </div>
  );

  if (!isControlled) {
    return content;
  }

  return (
    <FilterChipStateProvider
      filters={controlledFilters}
      filterDefinitions={controlledFilterDefinitions}
      onFiltersChange={onFiltersChange}
      sortables={sortables}
      sortableDefinitions={sortableDefinitions}
      onSortablesChange={onSortablesChange}
    >
      {content}
    </FilterChipStateProvider>
  );
}
